import { use, useState } from 'react';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';

import WanderingDetectionConfiguration from 'features/residents/models/configurations/wandering-detection-configuration';
import WanderingDetectionTimeFrameConfiguration from 'features/residents/models/configurations/wandering-detection-time-frame-configuration';
import { WanderingBoundary } from 'features/residents/models/wandering-boundary';
import ResidentSettingsProviderContext from 'features/residents/resident-settings/providers/contexts/resident-settings-provider-context';
import { useReadWanderingBoundariesForResident } from 'features/residents/resident-settings/providers/resident-queries';

type Props = {
  wanderingDetectionConfiguration: WanderingDetectionConfiguration;
  activeAllDay: boolean;
  index: number;
  valuesChanged: (newTimeFrameConfigurations: WanderingDetectionTimeFrameConfiguration[] | null, newActiveAllDay: boolean) => void;
};

export const useWanderingDetectionSettings = ({ wanderingDetectionConfiguration, activeAllDay, index, valuesChanged }: Props) => {
  const { setCustomErrorMessage } = use(ErrorMessageProviderContext);
  const [linkWanderingBoundaryModalIsOpen, setLinkWanderingBoundaryModalIsOpen] = useState<boolean>(false);
  const {
    detailEditState: { viewingMode, currentResidentSetting },
  } = use(ResidentSettingsProviderContext);
  const { data: wanderingBoundaries } = useReadWanderingBoundariesForResident(currentResidentSetting?.organisationUnitId ?? '');

  const openLinkWanderingBoundaryModal = () => {
    setLinkWanderingBoundaryModalIsOpen(true);
  };

  const closeLinkWanderingBoundaryModal = () => {
    setLinkWanderingBoundaryModalIsOpen(false);
  };

  const onWanderingBoundaryLinkClicked = (selectedWanderingBoundaries: WanderingBoundary[]) => {
    const newTimeFrameConfigurations = [...(wanderingDetectionConfiguration.wanderingDetectionTimeFrameConfigurations ?? [])];
    const newItemIds = selectedWanderingBoundaries.map((boundary) => boundary.id);

    if (!newTimeFrameConfigurations[index]) {
      setCustomErrorMessage('default');
      return;
    }

    newTimeFrameConfigurations[index].wanderingBoundaryLocationIds = [
      ...new Set([...newTimeFrameConfigurations[index].wanderingBoundaryLocationIds, ...newItemIds]),
    ];

    valuesChanged(newTimeFrameConfigurations, activeAllDay);
    closeLinkWanderingBoundaryModal();
  };

  const onUnlinkWanderingBoundaryClicked = (boundaryId: string) => {
    const newTimeFrameConfigurations = [...(wanderingDetectionConfiguration.wanderingDetectionTimeFrameConfigurations ?? [])];
    newTimeFrameConfigurations[index].wanderingBoundaryLocationIds = newTimeFrameConfigurations[index].wanderingBoundaryLocationIds.filter(
      (id) => id !== boundaryId,
    );
    valuesChanged(newTimeFrameConfigurations, activeAllDay);
  };

  return {
    linkWanderingBoundaryModalIsOpen,
    openLinkWanderingBoundaryModal,
    closeLinkWanderingBoundaryModal,
    onWanderingBoundaryLinkClicked,
    onUnlinkWanderingBoundaryClicked,
    wanderingBoundaries,
    viewingMode,
  };
};
